import { getActiveProjectId } from './projectContext';

const BASE = '';

// Merge the active project id (from the `/p/{id}` URL) into request headers
// as `X-Project-Id`. The middleware reads this to scope the request to the
// right project, so every API call is automatically project-aware without
// each endpoint having to thread the id through. No id (single-project /
// home screen) → no header → server falls back to the session default.
function withProjectHeaders(base?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...(base || {}) };
  const pid = getActiveProjectId();
  if (pid) headers['X-Project-Id'] = pid;
  return headers;
}

export async function fetchJSON<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${url}`, { headers: withProjectHeaders() });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export async function postJSON<T>(url: string, body?: unknown): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${url}`, {
      method: 'POST',
      headers: withProjectHeaders({ 'Content-Type': 'application/json' }),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export async function patchJSON<T>(url: string, body: unknown): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${url}`, {
      method: 'PATCH',
      headers: withProjectHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/** Result variant that preserves the server's error message (and HTTP
 *  status) instead of collapsing failures to `null`. Used where the UI
 *  needs to surface the upstream detail — e.g. Monco's super-admin 403
 *  on a status change. */
export interface JSONResult<T> {
  ok: boolean;
  data: T | null;
  error: string | null;
  /** HTTP status of the response; null when the request itself failed. */
  status: number | null;
}

async function requestJSONResult<T>(
  url: string,
  method: 'GET' | 'PATCH' | 'POST',
  body?: unknown,
): Promise<JSONResult<T>> {
  try {
    const res = await fetch(`${BASE}${url}`, {
      method,
      headers: withProjectHeaders(
        body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      ),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      /* empty / non-JSON body */
    }
    if (!res.ok) {
      const err = (data && typeof data === 'object' && 'error' in data)
        ? String((data as { error: unknown }).error)
        : `HTTP ${res.status}`;
      return { ok: false, data: null, error: err, status: res.status };
    }
    return { ok: true, data: data as T, error: null, status: res.status };
  } catch (e) {
    return { ok: false, data: null, error: String(e), status: null };
  }
}

export const fetchJSONResult = <T>(url: string) => requestJSONResult<T>(url, 'GET');
export const patchJSONResult = <T>(url: string, body: unknown) =>
  requestJSONResult<T>(url, 'PATCH', body);
export const postJSONResult = <T>(url: string, body?: unknown) =>
  requestJSONResult<T>(url, 'POST', body);

export async function deleteJSON<T>(url: string, body: unknown): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${url}`, {
      method: 'DELETE',
      headers: withProjectHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}
